import { api } from '../api';
/**
 * Pilot KPI Scorecard data feed (SPEC-FE-405).
 *
 * The backend doesn't expose a single /kpis endpoint — instead this
 * service fans out to the underlying counters + tracking endpoints and
 * folds the result into the scorecard shape the dashboard widgets
 * expect. The endpoint contract is the {@code SpectraIqKpiSnapshot}
 * server-side projection added in SPEC-BE-501.
 */
const toArray = (body) =>
  body?.data?.content
  ?? body?.content
  ?? (Array.isArray(body?.data) ? body.data : null)
  ?? (Array.isArray(body) ? body : []);

const unwrap = (r) => r?.data ?? r;

export default {
  pilotScorecard: (params) => api.get('/v1/dashboard/pilot-kpis', { params }).then(r => unwrap(r.data)),
  triageQueue:    (params) => api.get('/v1/triage/queue', { params }).then(r => toArray(r.data)),
  triageCall:     (id)     => api.get(`/v1/triage/calls/${id}`).then(r => r.data),
  triageSummary:  (params) => api.get('/v1/triage/summary', { params }).then(r => unwrap(r.data)),
  flagCall:       (id, body) => api.post(`/v1/triage/calls/${id}/flag`, body).then(r => unwrap(r.data)),
  assignCoaching: (id, body) => api.post(`/v1/triage/calls/${id}/coaching`, body).then(r => unwrap(r.data)),

  /** Filtered, paged queue: { content, page, size, totalElements, totalPages, counts }. */
  triageQueuePage: (params) => api.get('/v1/triage/queue', { params }).then(r => unwrap(r.data)),
  /** CSV of every call in the current filter, as a Blob. */
  triageExport:   (params) => api.get('/v1/triage/export', { params, responseType: 'blob' }).then(r => r.data),

  /* Review actions — each returns the updated call. */
  resolveCall:    (id, body) => api.post(`/v1/triage/calls/${id}/resolve`, body ?? {}).then(r => unwrap(r.data)),
  escalateCall:   (id, body) => api.post(`/v1/triage/calls/${id}/escalate`, body ?? {}).then(r => unwrap(r.data)),
  reopenCall:     (id)       => api.post(`/v1/triage/calls/${id}/reopen`).then(r => unwrap(r.data)),
  completeCoaching: (id)     => api.post(`/v1/triage/calls/${id}/coaching/complete`).then(r => unwrap(r.data)),
  /** { ids, action: RESOLVE|ESCALATE|PENDING|FLAG|UNFLAG|COACH, note?, reason?, coachUserId?, coachName?, dueDate? } */
  bulkReview:     (body)     => api.post('/v1/triage/calls/bulk', body).then(r => unwrap(r.data)),

  /* Call detail */
  annotations:    (id)       => api.get(`/v1/triage/calls/${id}/annotations`).then(r => unwrap(r.data)),
  annotate:       (id, body) => api.post(`/v1/triage/calls/${id}/annotations`, body).then(r => unwrap(r.data)),
  deleteAnnotation: (annotationId) => api.delete(`/v1/triage/annotations/${annotationId}`).then(r => unwrap(r.data)),
  retranscribe:   (id)       => api.post(`/v1/triage/calls/${id}/retranscribe`).then(r => unwrap(r.data)),

  /* Coaching library */
  libraryPatterns: (params)  => api.get('/v1/triage/library', { params }).then(r => unwrap(r.data)),
  createPattern:   (body)    => api.post('/v1/triage/library', body).then(r => unwrap(r.data)),
  archivePattern:  (id)      => api.delete(`/v1/triage/library/${id}`).then(r => unwrap(r.data)),
  libraryOverview: ()        => api.get('/v1/triage/library/overview').then(r => unwrap(r.data)),
  libraryExport:   ()        => api.get('/v1/triage/library/export', { responseType: 'blob' }).then(r => r.data),
};
